import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, RotateCcw, LogOut } from "lucide-react";
import LogoutConfirmModal from "./LogoutConfirmModal";
import { useLanguage } from "../contexts/LanguageContext";
import ZodiacGlyph from "./ZodiacGlyph";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const STORAGE_KEY = 'astral_belonging_state';

// Zodiac sign glyphs mapping (moved outside component for performance)
const ZODIAC_GLYPHS = {
    'aries': '♈',
    'taurus': '♉',
    'gemini': '♊',
    'cancer': '♋',
    'leo': '♌',
    'virgo': '♍',
    'libra': '♎',
    'scorpio': '♏',
    'sagittarius': '♐',
    'capricorn': '♑',
    'aquarius': '♒',
    'pisces': '♓'
};

// Zodiac sign translations
const ZODIAC_TRANSLATIONS = {
    en: {
        'aries': 'Aries', 'taurus': 'Taurus', 'gemini': 'Gemini', 'cancer': 'Cancer',
        'leo': 'Leo', 'virgo': 'Virgo', 'libra': 'Libra', 'scorpio': 'Scorpio',
        'sagittarius': 'Sagittarius', 'capricorn': 'Capricorn', 'aquarius': 'Aquarius', 'pisces': 'Pisces'
    },
    es: {
        'aries': 'Aries', 'taurus': 'Tauro', 'gemini': 'Géminis', 'cancer': 'Cáncer',
        'leo': 'Leo', 'virgo': 'Virgo', 'libra': 'Libra', 'scorpio': 'Escorpio',
        'sagittarius': 'Sagitario', 'capricorn': 'Capricornio', 'aquarius': 'Acuario', 'pisces': 'Piscis'
    }
};

// Planet/label translations
const LABEL_TRANSLATIONS = {
    en: { 'sun': 'SUN', 'moon': 'MOON', 'ascendant': 'ASCENDANT', 'rising': 'RISING' },
    es: { 'sun': 'SOL', 'moon': 'LUNA', 'ascendant': 'ASCENDENTE', 'rising': 'ASCENDENTE' }
};

// Zodiac dates for fallback calculation
const ZODIAC_DATES = [
    { sign: 'capricorn', start: [1, 1], end: [1, 19] },
    { sign: 'aquarius', start: [1, 20], end: [2, 18] },
    { sign: 'pisces', start: [2, 19], end: [3, 20] },
    { sign: 'aries', start: [3, 21], end: [4, 19] },
    { sign: 'taurus', start: [4, 20], end: [5, 20] },
    { sign: 'gemini', start: [5, 21], end: [6, 20] },
    { sign: 'cancer', start: [6, 21], end: [7, 22] },
    { sign: 'leo', start: [7, 23], end: [8, 22] },
    { sign: 'virgo', start: [8, 23], end: [9, 22] },
    { sign: 'libra', start: [9, 23], end: [10, 22] },
    { sign: 'scorpio', start: [10, 23], end: [11, 21] },
    { sign: 'sagittarius', start: [11, 22], end: [12, 21] },
    { sign: 'capricorn', start: [12, 22], end: [12, 31] }
];

/**
 * Calculate zodiac from birth date (fallback only)
 */
const getZodiacSign = (dateStr) => {
    if (!dateStr) return 'aries';
    const [, month, day] = dateStr.split('-').map(Number);
    
    for (const z of ZODIAC_DATES) {
        const [sm, sd] = z.start;
        const [em, ed] = z.end;
        if ((month === sm && day >= sd) || (month === em && day <= ed)) {
            return z.sign;
        }
    }
    return 'aries';
};

/**
 * SignCard - Vertical stack layout for Big Three display
 * Now with fade animation support for sign transitions and SVG glyphs
 */
const SignCard = ({ sign, label, isLocked = false, isTransitioning = false, language = 'en' }) => {
    const signLower = sign?.toLowerCase() || 'aries';
    
    // Get translated sign name
    const translations = ZODIAC_TRANSLATIONS[language] || ZODIAC_TRANSLATIONS.en;
    const displayName = translations[signLower] || sign || '—';
    
    // Get translated label
    const labelTranslations = LABEL_TRANSLATIONS[language] || LABEL_TRANSLATIONS.en;
    const labelLower = label?.toLowerCase() || '';
    const displayLabel = labelTranslations[labelLower] || label;
    
    return (
        <div className="flex flex-col items-center min-w-[90px]">
            {/* SIGN NAME - Top */}
            <motion.span 
                className="text-xs font-inter font-semibold uppercase tracking-wider mb-2 whitespace-nowrap"
                style={{ color: isLocked ? 'rgba(212, 175, 55, 0.3)' : '#D4AF37' }}
                animate={{ opacity: isTransitioning ? 0 : 1 }}
                transition={{ duration: 0.3 }}
            >
                {isLocked ? '?' : displayName}
            </motion.span>
            
            {/* ZODIAC ICON - Middle - Now using SVG ZodiacGlyph */}
            <motion.div 
                className="mb-2 flex items-center justify-center"
                style={{ 
                    opacity: isLocked ? 0.3 : 1
                }}
                title={isLocked ? `${label}: Birth time required` : `${label} in ${displayName}`}
                animate={{ opacity: isTransitioning ? 0 : 1, scale: isTransitioning ? 0.8 : 1 }}
                transition={{ duration: 0.3 }}
            >
                {isLocked ? (
                    <span className="text-3xl" style={{ color: 'rgba(212, 175, 55, 0.3)' }}>🔒</span>
                ) : (
                    <ZodiacGlyph 
                        sign={signLower} 
                        size={40} 
                        variant="gold" 
                        glow={true}
                        instance={`header-${label}`}
                    />
                )}
            </motion.div>
            
            {/* PLACEMENT LABEL - Bottom */}
            <span 
                className="text-[10px] font-inter uppercase tracking-[0.2em]"
                style={{ color: 'rgba(212, 175, 55, 0.5)' }}
            >
                {displayLabel}
            </span>
        </div>
    );
};

/**
 * Ascending Moon Animation Component
 * Displays during Cosmic Sync - moon rises from center to top
 */
const AscendingMoon = ({ isActive, onComplete }) => {
    return (
        <AnimatePresence>
            {isActive && (
                <motion.div
                    className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    {/* Subtle dark overlay */}
                    <motion.div 
                        className="absolute inset-0 bg-black/40"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    />
                    
                    {/* Rising Moon */}
                    <motion.div
                        className="relative z-10"
                        initial={{ y: 100, opacity: 0, scale: 0.5 }}
                        animate={{ 
                            y: [-100, -250], 
                            opacity: [0, 1, 1, 0],
                            scale: [0.5, 1.2, 1, 0.8]
                        }}
                        transition={{ 
                            duration: 2.5,
                            times: [0, 0.3, 0.7, 1],
                            ease: "easeOut"
                        }}
                        onAnimationComplete={onComplete}
                    >
                        {/* Moon glow effect */}
                        <div 
                            className="absolute inset-0 rounded-full blur-xl"
                            style={{
                                background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)',
                                transform: 'scale(2)'
                            }}
                        />
                        
                        {/* Moon symbol */}
                        <span 
                            className="text-7xl relative"
                            style={{
                                color: '#F5D76E',
                                textShadow: '0 0 40px rgba(212,175,55,0.8), 0 0 80px rgba(212,175,55,0.4)'
                            }}
                        >
                            ☽
                        </span>
                    </motion.div>
                    
                    {/* Rising text */}
                    <motion.p
                        className="absolute bottom-1/3 text-center text-primary/80 font-cinzel text-lg tracking-widest"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: [0, 1, 1, 0], y: [20, 0, 0, -20] }}
                        transition={{ duration: 2.5, times: [0, 0.2, 0.8, 1] }}
                    >
                        Re-aligning with the cosmos...
                    </motion.p>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

/**
 * Debug Overlay Component
 * Shows calculation details - triggered by 5-tap secret gesture
 */
const DebugOverlay = ({ isVisible, debugData, onClose }) => {
    if (!isVisible || !debugData) return null;
    
    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    className="fixed bottom-4 left-4 right-4 z-40"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.3 }}
                >
                    <div 
                        className="max-w-md mx-auto p-4 rounded-lg font-mono text-xs"
                        style={{
                            background: 'rgba(10, 10, 11, 0.95)',
                            border: '1px solid rgba(212, 175, 55, 0.3)',
                            backdropFilter: 'blur(8px)'
                        }}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-primary/60 uppercase tracking-wider text-[10px]">
                                ⚙ Math Truth
                            </span>
                            <button 
                                onClick={onClose}
                                className="text-primary/40 hover:text-primary transition-colors"
                            >
                                ✕
                            </button>
                        </div>
                        
                        {/* Debug content */}
                        <pre 
                            className="text-[11px] leading-relaxed overflow-x-auto"
                            style={{ color: 'rgba(212, 175, 55, 0.9)' }}
                        >
{`Input: ${debugData.location || 'Unknown'}
Detected: ${debugData.latitude?.toFixed(4) || '—'}, ${debugData.longitude?.toFixed(4) || '—'}
Time: ${debugData.utc_time || '—'} UTC
Offset: UTC${debugData.timezone_offset >= 0 ? '+' : ''}${debugData.timezone_offset || 0}
Engine: Swiss Ephemeris (Tropical/Placidus)
──────────────────────────────
Sun: ${debugData.sun || '—'}
Moon: ${debugData.moon || '—'}
Ascendant: ${debugData.rising || '—'} (${debugData.asc_degree?.toFixed(2) || '—'}°)`}
                        </pre>
                        
                        {/* Timestamp */}
                        <div className="mt-3 pt-2 border-t border-primary/10">
                            <span className="text-primary/30 text-[9px]">
                                Last sync: {debugData.timestamp || 'Never'}
                            </span>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

/**
 * Reset Profile Confirmation Modal
 * Allows users to clear all data and restart onboarding
 */
const ResetConfirmModal = ({ isVisible, onConfirm, onCancel, language = 'en' }) => {
    if (!isVisible) return null;
    
    // Modal translations
    const text = {
        en: {
            title: 'Reset Your Journey?',
            description: 'This will clear your birth data and return you to the beginning. Use this to fix incorrect date, time, or location.',
            cancel: 'Cancel',
            confirm: 'Reset & Restart'
        },
        es: {
            title: '¿Reiniciar tu Viaje?',
            description: 'Esto borrará tus datos de nacimiento y te llevará al inicio. Úsalo para corregir fecha, hora o ubicación incorrectas.',
            cancel: 'Cancelar',
            confirm: 'Reiniciar'
        }
    };
    
    const t = text[language] || text.en;
    
    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    {/* Backdrop */}
                    <motion.div 
                        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                        onClick={onCancel}
                    />
                    
                    {/* Modal */}
                    <motion.div
                        className="relative z-10 w-full max-w-sm p-6 rounded-xl"
                        style={{
                            background: 'linear-gradient(135deg, rgba(15, 15, 17, 0.98) 0%, rgba(10, 10, 12, 0.98) 100%)',
                            border: '1px solid rgba(212, 175, 55, 0.3)',
                            boxShadow: '0 0 40px rgba(212, 175, 55, 0.1)'
                        }}
                        initial={{ scale: 0.9, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.9, y: 20 }}
                    >
                        {/* Icon */}
                        <div className="flex justify-center mb-4">
                            <div 
                                className="w-16 h-16 rounded-full flex items-center justify-center"
                                style={{ 
                                    background: 'rgba(212, 175, 55, 0.1)',
                                    border: '2px solid rgba(212, 175, 55, 0.3)'
                                }}
                            >
                                <RotateCcw className="w-8 h-8 text-primary" />
                            </div>
                        </div>
                        
                        {/* Title */}
                        <h3 className="font-cinzel text-xl text-center text-primary mb-2">
                            {t.title}
                        </h3>
                        
                        {/* Description */}
                        <p className="text-center text-primary/60 text-sm font-inter mb-6">
                            {t.description}
                        </p>
                        
                        {/* Buttons */}
                        <div className="flex gap-3">
                            <button
                                onClick={onCancel}
                                className="flex-1 py-3 px-4 rounded-lg font-inter text-sm transition-all"
                                style={{
                                    background: 'rgba(212, 175, 55, 0.1)',
                                    border: '1px solid rgba(212, 175, 55, 0.2)',
                                    color: 'rgba(212, 175, 55, 0.7)'
                                }}
                            >
                                {t.cancel}
                            </button>
                            <button
                                onClick={onConfirm}
                                className="flex-1 py-3 px-4 rounded-lg font-inter text-sm font-medium transition-all hover:brightness-110"
                                style={{
                                    background: 'linear-gradient(135deg, #D4AF37 0%, #AA8C2C 100%)',
                                    color: '#0A0A0B'
                                }}
                                data-testid="confirm-reset-btn"
                            >
                                {t.confirm}
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

/**
 * CosmicIdentityHeader - User name with Sun/Moon/Ascendant signs
 * 
 * FEATURES:
 * - Cosmic Sync button with Ascending Moon animation
 * - Hidden Debug Mode (5-tap secret gesture on user name)
 * - Reset Profile button to restart onboarding
 * - Swiss Ephemeris calculations via backend
 */
export const CosmicIdentityHeader = ({ userData, onRefresh, onReset, onLogout, isAuthenticated }) => {
    const { t, language } = useLanguage();
    const [bigThree, setBigThree] = useState({
        sun: null,
        moon: null,
        ascendant: null,
        loading: true,
        error: null,
        lastSync: null
    });
    const [syncing, setSyncing] = useState(false);
    const [showMoonAnimation, setShowMoonAnimation] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [showResetModal, setShowResetModal] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);
    
    // Debug mode state
    const [showDebug, setShowDebug] = useState(false);
    const [debugData, setDebugData] = useState(null);
    
    // 5-tap secret gesture tracking
    const tapCountRef = useRef(0);
    const tapTimerRef = useRef(null);
    const initialFetchDone = useRef(false);
    const TAP_THRESHOLD = 5;
    const TAP_TIMEOUT = 1000; // Must complete 5 taps within 1 second
    
    // Cache key for Big Three
    const BIG_THREE_CACHE_KEY = 'astrum_big_three_cache';

    // Handle secret gesture (5 rapid taps)
    const handleSecretTap = useCallback(() => {
        tapCountRef.current += 1;
        
        // Clear existing timer
        if (tapTimerRef.current) {
            clearTimeout(tapTimerRef.current);
        }
        
        // Check if threshold reached
        if (tapCountRef.current >= TAP_THRESHOLD) {
            setShowDebug(prev => !prev);
            tapCountRef.current = 0;
            console.log('🔧 Debug mode toggled');
            return;
        }
        
        // Set timeout to reset counter
        tapTimerRef.current = setTimeout(() => {
            tapCountRef.current = 0;
        }, TAP_TIMEOUT);
    }, []);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (tapTimerRef.current) {
                clearTimeout(tapTimerRef.current);
            }
        };
    }, []);
    
    // Load cached Big Three on mount
    useEffect(() => {
        try {
            const cached = localStorage.getItem(BIG_THREE_CACHE_KEY);
            if (cached) {
                const { sun, moon, ascendant, birthDate, lastSync } = JSON.parse(cached);
                // Only use cache if birth date matches
                if (birthDate === userData?.birthDate && sun && moon) {
                    setBigThree({
                        sun, moon, ascendant,
                        loading: false,
                        error: null,
                        lastSync: lastSync || new Date().toISOString()
                    });
                    console.log('⚡ Big Three loaded from cache');
                    initialFetchDone.current = true;
                }
            }
        } catch (e) {
            console.warn('Could not load Big Three cache');
        }
    }, [userData?.birthDate]);

    // Fetch the computed Big Three from backend
    const fetchBigThree = useCallback(async (showAnimation = false) => {
        if (!userData?.birthDate) {
            setBigThree(prev => ({ ...prev, loading: false }));
            return;
        }

        // Start moon animation if requested
        if (showAnimation) {
            setShowMoonAnimation(true);
            setIsTransitioning(true);
        }

        try {
            // Get location coordinates
            let locationDisplay = '';
            let lat = null;
            let lng = null;
            let utcOffsetMinutes = null;
            
            if (typeof userData.location === 'object' && userData.location.lat) {
                locationDisplay = userData.location.display || '';
                lat = userData.location.lat;
                lng = userData.location.lng;
                utcOffsetMinutes = userData.location.utc_offset_minutes;
            } else {
                locationDisplay = typeof userData.location === 'string' ? userData.location : (userData.location?.display || '');
                lat = userData.locationLat || userData.location_lat;
                lng = userData.locationLng || userData.location_lng;
                utcOffsetMinutes = userData.utcOffsetMinutes || userData.utc_offset_minutes;
            }

            const hasCoordinates = lat && lng;
            const hasBirthTime = userData.birthTime && !userData.unknownTime;

            const requestBody = {
                birth_date: userData.birthDate,
                birth_time: userData.birthTime || "12:00",
                birth_location: locationDisplay || "London, UK"
            };
            
            if (hasCoordinates) {
                requestBody.lat = lat;
                requestBody.lng = lng;
                if (utcOffsetMinutes != null) {
                    requestBody.utc_offset_minutes = utcOffsetMinutes;
                }
                console.log('📍 Cosmic Sync - Using coordinates:', requestBody.lat, requestBody.lng);
            }

            const response = await fetch(`${API_URL}/api/calculate-chart`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(requestBody)
            });

            if (response.ok) {
                const data = await response.json();
                if (data.status === 'success') {
                    console.log('✨ Chart calculated:', data.big_three);
                    
                    // Update debug data
                    setDebugData({
                        location: locationDisplay,
                        latitude: lat,
                        longitude: lng,
                        utc_time: data.debug?.utc_datetime?.replace('T', ' ').replace('+00:00', ''),
                        timezone_offset: data.debug?.timezone_offset,
                        timezone_name: data.debug?.timezone_name,
                        sun: data.big_three.sun,
                        moon: data.big_three.moon,
                        rising: data.big_three.rising,
                        asc_degree: data.ascendant?.longitude,
                        engine: data.debug?.engine,
                        house_system: data.debug?.house_system,
                        timestamp: new Date().toLocaleString()
                    });
                    
                    const newBigThree = {
                        sun: data.big_three.sun,
                        moon: data.big_three.moon,
                        ascendant: hasBirthTime ? data.big_three.rising : null,
                        loading: false,
                        error: null,
                        lastSync: new Date().toISOString()
                    };
                    
                    // Cache the result for instant load next time
                    try {
                        localStorage.setItem(BIG_THREE_CACHE_KEY, JSON.stringify({
                            ...newBigThree,
                            birthDate: userData.birthDate
                        }));
                        console.log('💾 Big Three cached');
                    } catch (e) {
                        console.warn('Could not cache Big Three');
                    }
                    
                    // Wait for animation to complete before updating signs
                    if (showAnimation) {
                        // Signs will be updated when animation completes
                        setBigThree(prev => ({
                            ...prev,
                            _pending: {
                                sun: data.big_three.sun,
                                moon: data.big_three.moon,
                                ascendant: hasBirthTime ? data.big_three.rising : null
                            }
                        }));
                    } else {
                        setBigThree(newBigThree);
                    }
                    
                    if (onRefresh) {
                        onRefresh(data);
                    }
                    return;
                }
            }
            
            // Fallback
            const sunSign = getZodiacSign(userData.birthDate);
            const fallbackData = {
                sun: sunSign.charAt(0).toUpperCase() + sunSign.slice(1),
                moon: null,
                ascendant: null,
                loading: false,
                error: "Unable to calculate Moon and Ascendant"
            };
            
            if (showAnimation) {
                setBigThree(prev => ({ ...prev, _pending: fallbackData }));
            } else {
                setBigThree(fallbackData);
            }
            
        } catch (err) {
            console.error('Failed to fetch Big Three:', err);
            const sunSign = getZodiacSign(userData.birthDate);
            setBigThree({
                sun: sunSign.charAt(0).toUpperCase() + sunSign.slice(1),
                moon: null,
                ascendant: null,
                loading: false,
                error: err.message
            });
            setShowMoonAnimation(false);
            setIsTransitioning(false);
        }
    }, [userData, onRefresh]);

    // Handle moon animation complete
    const handleMoonAnimationComplete = useCallback(() => {
        setShowMoonAnimation(false);
        
        // Apply pending data after animation
        setBigThree(prev => {
            if (prev._pending) {
                return {
                    ...prev._pending,
                    loading: false,
                    error: null,
                    lastSync: new Date().toISOString(),
                    _pending: undefined
                };
            }
            return prev;
        });
        
        // Brief delay then reveal new signs
        setTimeout(() => {
            setIsTransitioning(false);
            setSyncing(false);
        }, 300);
    }, []);

    // Initial fetch on mount (if not already loaded from cache)
    useEffect(() => {
        const initializeBigThree = async () => {
            if (!initialFetchDone.current && userData?.birthDate) {
                initialFetchDone.current = true;
                await fetchBigThree(false);
            }
        };
        initializeBigThree();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData?.birthDate]);

    // Handle manual sync with animation
    const handleSync = () => {
        if (syncing || bigThree.loading) return;
        
        console.log('🔄 Manual Cosmic Sync triggered');
        setSyncing(true);
        fetchBigThree(true); // true = show animation
    };

    // Handle profile reset
    const handleResetConfirm = () => {
        console.log('🔄 Resetting user profile...');
        
        // Clear localStorage
        localStorage.removeItem(STORAGE_KEY);
        
        // Close modal
        setShowResetModal(false);
        
        // Trigger page reload to restart onboarding
        if (onReset) {
            onReset();
        } else {
            // Fallback: reload the page
            window.location.reload();
        }
    };

    // Handle logout confirmation
    const handleLogoutConfirm = async () => {
        setLoggingOut(true);
        if (onLogout) {
            await onLogout();
        }
        setShowLogoutModal(false);
        setLoggingOut(false);
    };

    const userName = userData?.name || 'Child of the Stars';
    const hasAscendant = bigThree.ascendant && userData?.birthTime && !userData?.unknownTime;

    return (
        <>
            {/* Ascending Moon Animation Overlay */}
            <AscendingMoon 
                isActive={showMoonAnimation} 
                onComplete={handleMoonAnimationComplete}
            />
            
            {/* Debug Overlay (5-tap secret gesture to toggle) */}
            <DebugOverlay 
                isVisible={showDebug} 
                debugData={debugData}
                onClose={() => setShowDebug(false)}
            />
            
            {/* Reset Profile Confirmation Modal */}
            <ResetConfirmModal
                isVisible={showResetModal}
                onConfirm={handleResetConfirm}
                onCancel={() => setShowResetModal(false)}
                language={language}
            />
            
            {/* Logout Confirmation Modal (Ruthless Coach) */}
            <LogoutConfirmModal
                isOpen={showLogoutModal}
                onConfirm={handleLogoutConfirm}
                onCancel={() => setShowLogoutModal(false)}
                isLoading={loggingOut}
            />
            
            <motion.div 
                className="w-full px-4 py-6"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                {/* Cosmic Identity Label - Clean and mystical */}
                <div className="text-center mb-3">
                    <p 
                        className="text-[10px] font-inter text-primary/40 uppercase tracking-[0.3em] cursor-default select-none"
                        onClick={handleSecretTap}
                    >
                        {t('dashboard.cosmic_identity')}
                    </p>
                </div>
                
                {/* User Name - Also a secret tap target */}
                <h1 
                    className="font-cinzel text-2xl sm:text-3xl text-center mb-5 cursor-default select-none"
                    style={{
                        background: 'linear-gradient(135deg, #F5D76E 0%, #D4AF37 50%, #AA8C2C 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                    }}
                    onClick={handleSecretTap}
                >
                    {userName}
                </h1>
                
                {/* Big Three Signs - Vertical Stack Layout */}
                {bigThree.loading ? (
                    <div className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                        <span className="text-xs text-primary/50 font-inter">{t('dashboard.calculating_positions')}</span>
                    </div>
                ) : (
                    <div className="flex items-center justify-center gap-4 sm:gap-8 px-2">
                        {/* Sun Sign */}
                        <SignCard sign={bigThree.sun} label="SUN" isTransitioning={isTransitioning} language={language} />
                        
                        {/* Decorative separator */}
                        <div className="w-px h-10 sm:h-14 bg-gradient-to-b from-transparent via-primary/30 to-transparent flex-shrink-0" />
                        
                        {/* Moon Sign */}
                        <SignCard sign={bigThree.moon} label="MOON" isLocked={!bigThree.moon} isTransitioning={isTransitioning} language={language} />
                        
                        {/* Decorative separator */}
                        <div className="w-px h-10 sm:h-14 bg-gradient-to-b from-transparent via-primary/30 to-transparent flex-shrink-0" />
                        
                        {/* Ascendant */}
                        <SignCard sign={bigThree.ascendant} label="ASCENDANT" isLocked={!hasAscendant} isTransitioning={isTransitioning} language={language} />
                    </div>
                )}
                
                {/* Subtle divider */}
                <div className="mt-6 mx-auto w-32 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
            </motion.div>
        </>
    );
};

export default CosmicIdentityHeader;
